/**
 * syncManager.js
 * Gestor de eventos de conexión online/offline y sincronización en cola FIFO hacia Laravel.
 */

import offlineDbService from "./offlineDbService";
import axios from "axios";
import alertService from "./alertService";

class SyncManager {
    constructor() {
        this.store = null;
        this.isSyncing = false;
        this.initialized = false;
    }

    /**
     * Inicializa el monitor de estado de red con el Vuex store.
     */
    init(store) {
        if (this.initialized) return;
        this.store = store;
        this.initialized = true;

        // Actualizar contador inicial de pendientes
        this.updatePendingCount();

        // Eventos nativos de red
        window.addEventListener('online', () => {
            console.log('[SyncManager] Conexión a Internet reestablecida.');
            if (this.store) {
                this.store.commit('offlineSync/setOnline', true);
            }
            alertService.info("Conexión reestablecida. Verificando sincronización de pedidos...");
            
            // Reportar reconexión al log de actividades
            try {
                axios.post('/admin/activity-logs/report-network', {
                    status: 'online',
                    client_time: new Date().toISOString()
                }).catch(() => {});
            } catch (e) {}

            this.syncPendingOrders();
        });

        window.addEventListener('offline', () => {
            console.log('[SyncManager] Se ha perdido la conexión a Internet.');
            if (this.store) {
                this.store.commit('offlineSync/setOnline', false);
            }
            alertService.warning("Modo Fuera de Línea activo. Las ventas se guardarán localmente.");

            // Guardar reporte local o encolar para cuando reconecte
            try {
                offlineDbService.enqueueOrder({
                    event_type: 'network',
                    action: 'network_offline',
                    title: 'Conexión a Red Suspendida',
                    description: 'Pérdida de conectividad detectada por el navegador.',
                    client_time: new Date().toISOString()
                }).catch(() => {});
            } catch (e) {}
        });

        // Revisión periódica por si el navegador no emite el evento online en fallos intermitentes
        setInterval(() => {
            if (navigator.onLine && !this.isSyncing) {
                this.checkAndSync();
            }
        }, 30000);
    }

    async updatePendingCount() {
        try {
            const count = await offlineDbService.countPendingOrders();
            if (this.store) {
                this.store.commit('offlineSync/setPendingCount', count);
            }
            return count;
        } catch (e) {
            console.error("Error al contar órdenes pendientes:", e);
            return 0;
        }
    }

    async checkAndSync() {
        const count = await this.updatePendingCount();
        if (count > 0 && navigator.onLine && !this.isSyncing) {
            await this.syncPendingOrders();
        }
    }

    /**
     * Sincroniza las órdenes pendientes contra el backend.
     */
    async syncPendingOrders() {
        if (this.isSyncing) return;
        if (!navigator.onLine) {
            console.warn('[SyncManager] Intento de sincronización cancelado: sin conexión.');
            return;
        }

        const pendingOrders = await offlineDbService.getPendingOrders();
        if (!pendingOrders || pendingOrders.length === 0) {
            await this.updatePendingCount();
            return;
        }

        this.isSyncing = true;
        if (this.store) {
            this.store.commit('offlineSync/setSyncing', true);
        }

        console.log(`[SyncManager] Iniciando sincronización de ${pendingOrders.length} orden(es)...`);

        let successCount = 0;

        for (const order of pendingOrders) {
            try {
                // Envío individual o en lote con UUID
                const response = await axios.post('admin/pos', {
                    ...order,
                    client_uuid: order.client_uuid,
                    is_offline_sync: true
                });

                if (response.data && response.data.data) {
                    const serverId = response.data.data.id;
                    await offlineDbService.markOrderSynced(order.client_uuid, serverId);
                    successCount++;
                }
            } catch (error) {
                console.error(`[SyncManager] Error al sincronizar orden ${order.client_uuid}:`, error);
                // Si el error es 409 o indica que ya existe con ese UUID, se marca sincronizada para no atascar la cola
                if (error.response && error.response.status === 409) {
                    await offlineDbService.markOrderSynced(order.client_uuid, 'already-processed');
                    successCount++;
                }
            }
        }

        this.isSyncing = false;
        if (this.store) {
            this.store.commit('offlineSync/setSyncing', false);
        }

        await this.updatePendingCount();

        if (successCount > 0) {
            alertService.success(`${successCount} orden(es) offline sincronizada(s) con éxito en el servidor.`);
        }
    }
}

export default new SyncManager();
